import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { RootState } from "../redux/store";
import {
  publicSendOtp,
  publicVerifyOtp,
} from "../redux/slices/publicAuthSlice";
import { fetchEligibleClubsByPhone } from "../redux/slices/groupSlice";

const CLUB_REGISTER_URL =
  "https://club.bsangam.com/client-register";

const LoginPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const { loading } = useSelector(
    (state: RootState) => state.publicAuth
  );

  const [mobile, setMobile] = useState("");
  const [otpValues, setOtpValues] = useState([
    "",
    "",
    "",
    "",
  ]);
  const [showOtp, setShowOtp] = useState(false);

  const otp = otpValues.join("");

  const handleOtpChange = (
    value: string,
    index: number
  ) => {
    const cleanValue = value.replace(/\D/g, "");

    if (!cleanValue) {
      const updatedOtp = [...otpValues];
      updatedOtp[index] = "";
      setOtpValues(updatedOtp);
      return;
    }

    const updatedOtp = [...otpValues];
    updatedOtp[index] = cleanValue.slice(-1);
    setOtpValues(updatedOtp);

    if (index < otpValues.length - 1) {
      const nextInput = document.getElementById(
        `otp-${index + 1}`
      );

      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (
      event.key === "Backspace" &&
      !otpValues[index] &&
      index > 0
    ) {
      const previousInput = document.getElementById(
        `otp-${index - 1}`
      );

      previousInput?.focus();
    }

    if (event.key === "Enter") {
      handleVerifyOtp();
    }
  };

  const handleOtpPaste = (
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();

    const pastedValue = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);

    if (!pastedValue) return;

    const updatedOtp = ["", "", "", ""];

    pastedValue.split("").forEach((digit, index) => {
      updatedOtp[index] = digit;
    });

    setOtpValues(updatedOtp);

    const lastIndex = Math.min(
      pastedValue.length - 1,
      3
    );

    document
      .getElementById(`otp-${lastIndex}`)
      ?.focus();
  };

  const handleSendOtp = async () => {
    if (!mobile || mobile.length !== 10) {
      toast.error("Enter a valid mobile number");
      return;
    }

    localStorage.setItem("public_mobile", mobile);

    const response = await dispatch(
      publicSendOtp(mobile)
    );

    if (publicSendOtp.fulfilled.match(response)) {
      toast.success("OTP sent successfully");

      setShowOtp(true);
      setOtpValues(["", "", "", ""]);

      setTimeout(() => {
        document
          .getElementById("otp-0")
          ?.focus();
      }, 100);

      return;
    }

    toast.error(
      (response.payload as string) ||
        "Failed to send OTP"
    );
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 4) {
      toast.error("Enter a valid OTP");
      return;
    }

    const response = await dispatch(
      publicVerifyOtp({
        phone_number: mobile,
        otp,
      })
    );

    if (!publicVerifyOtp.fulfilled.match(response)) {
      toast.error(
        (response.payload as string) ||
          "OTP verification failed"
      );

      return;
    }

    const data: any = response.payload;

    if (data?.user_exists === true) {
      const user = data?.user;
      const token = data?.token;

      if (!user?.user_id) {
        toast.error("User data is missing");
        return;
      }

      localStorage.setItem("token", token || "");
      localStorage.setItem(
        "user",
        JSON.stringify(user)
      );

      localStorage.setItem(
        "public_mobile",
        user.phone_number || mobile
      );

      localStorage.removeItem("server_otp");
      localStorage.removeItem("login_response");

      const selectedClient = JSON.parse(
        localStorage.getItem("selectedClient") ||
          "null"
      );

      toast.success("Login successful");

      if (selectedClient?.client_id) {
        navigate("/home");
        return;
      }

      const clubResponse = await dispatch(
        fetchEligibleClubsByPhone(
          user.phone_number || mobile
        )
      );

      const pendingClubs = Array.isArray(
        clubResponse.payload?.data
      )
        ? clubResponse.payload.data
        : [];

      if (pendingClubs.length > 0) {
        navigate("/join-club");
      } else {
        navigate("/home");
      }

      return;
    }

    localStorage.setItem(
      "public_mobile",
      mobile
    );

    toast.info("Please complete your profile");
    navigate("/complete-profile");
  };

  const handleRegisterClick = () => {
    if (mobile) {
      localStorage.setItem(
        "public_mobile",
        mobile
      );
    }

    navigate("/complete-profile");
  };

  const handleChangeMobile = () => {
    setShowOtp(false);
    setOtpValues(["", "", "", ""]);
  };

  const features = [
    {
      icon: "feather-users",
      title: "Clubs & Communities",
      text:
        "Connect with clubs, communities and people in one place.",
    },
    {
      icon: "feather-file-text",
      title: "Posts & Updates",
      text:
        "Stay updated with announcements, news, events and activities.",
    },
    {
      icon: "feather-heart",
      title: "Causes & Events",
      text:
        "Follow meaningful causes and participate in local programs.",
    },
  ];

  return (
    <div className="bs-auth-page">
      <div className="bs-auth-shell">
        <div className="bs-auth-left">
          <div className="bs-auth-left-inner">
            <div className="bs-chip">
              <span />
              BHARAT SANGAM
            </div>

            <h1>
              Connect with clubs,
              <br />
              communities and
              <br />
              members.
            </h1>

            <p className="bs-left-subtitle">
              Bharat Sangam brings club updates,
              member activities, events and community
              engagement into one simple platform.
            </p>

            <div className="bs-feature-stack">
              {features.map((item) => (
                <div
                  className="bs-feature-box"
                  key={item.title}
                >
                  <div className="bs-feature-box-icon">
                    <i className={item.icon} />
                  </div>

                  <div className="bs-feature-box-content">
                    <h5>{item.title}</h5>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bs-auth-right">
          <div className="bs-auth-card">
            <div className="bs-logo-wrap">
              <img
                src="/assets/images/bharat_sangam.png"
                alt="Bharat Sangam"
              />
            </div>

            <div className="bs-auth-title">
              <h2>
                {showOtp
                  ? "Verify OTP"
                  : "Login to continue"}
              </h2>

              <p>
                {showOtp
                  ? `Enter the OTP sent to +91 ${mobile}`
                  : "Use your registered mobile number to access Bharat Sangam."}
              </p>
            </div>

            <div className="bs-steps">
              <div
                className={`bs-step-dot ${
                  !showOtp ? "active" : "done"
                }`}
              >
                {!showOtp ? (
                  "1"
                ) : (
                  <i className="feather-check" />
                )}
              </div>

              <div
                className={`bs-step-line ${
                  showOtp ? "active" : ""
                }`}
              />

              <div
                className={`bs-step-dot ${
                  showOtp ? "active" : ""
                }`}
              >
                2
              </div>
            </div>

            {!showOtp ? (
              <>
                <div className="bs-form-group">
                  <label>Mobile Number</label>

                  <div className="bs-phone-field">
                    <span>+91</span>

                    <input
                      type="tel"
                      inputMode="numeric"
                      value={mobile}
                      onChange={(event) =>
                        setMobile(
                          event.target.value.replace(
                            /\D/g,
                            ""
                          )
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          handleSendOtp();
                        }
                      }}
                      maxLength={10}
                      placeholder="Enter mobile number"
                      autoComplete="tel"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="bs-main-btn"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Sending OTP...
                    </>
                  ) : (
                    "Send OTP"
                  )}
                </button>
              </>
            ) : (
              <>
                <div className="bs-form-group">
                  <label>OTP</label>

                  <div className="bs-otp-boxes">
                    {otpValues.map(
                      (digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(event) =>
                            handleOtpChange(
                              event.target.value,
                              index
                            )
                          }
                          onKeyDown={(event) =>
                            handleOtpKeyDown(
                              event,
                              index
                            )
                          }
                          onPaste={handleOtpPaste}
                          autoComplete={
                            index === 0
                              ? "one-time-code"
                              : "off"
                          }
                          className="bs-otp-box"
                        />
                      )
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={loading}
                  className="bs-main-btn"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Verifying...
                    </>
                  ) : (
                    "Verify OTP"
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleChangeMobile}
                  className="bs-text-btn"
                >
                  Change mobile number
                </button>
              </>
            )}

            <div className="bs-divider">
              <span />
              <p>New to Bharat Sangam?</p>
              <span />
            </div>

            <div className="bs-account-options">
              <button
                type="button"
                onClick={handleRegisterClick}
                className="bs-account-option"
              >
                <span className="bs-account-icon">
                  <i className="feather-user" />
                </span>

                <span className="bs-account-content">
                  <strong>
                    Create Personal Account
                  </strong>

                  <small>
                    Join clubs, follow updates and
                    connect with people
                  </small>
                </span>

                <i className="feather-chevron-right bs-account-arrow" />
              </button>

              <a
                href={CLUB_REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bs-account-option"
              >
                <span className="bs-account-icon">
                  <i className="feather-users" />
                </span>

                <span className="bs-account-content">
                  <strong>
                    Register Your Club
                  </strong>

                  <small>
                    Create and manage your club or
                    organization
                  </small>
                </span>

                <i className="feather-external-link bs-account-arrow" />
              </a>
            </div>

            <button
              type="button"
              onClick={() => navigate("/home")}
              className="bs-guest-link"
            >
              Continue as Guest
              <i className="feather-arrow-right" />
            </button>
          </div>

          <div className="bs-auth-footer">
            <div className="bs-footer-links">
              <Link to="/about-us">
                Home
              </Link>

              <Link to="/features">
                Features
              </Link>

              <Link to="/terms-and-conditions">
                Terms
              </Link>

              <Link to="/privacy-policy">
                Privacy
              </Link>

              <Link to="/return-cancellation-policy">
                Cancellation
              </Link>

              <Link to="/delete-account">
                Delete Account
              </Link>
            </div>

            <p>
              © 2026 POLITICA BHARAT CORPORATION
              LLP. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .bs-auth-page {
          min-height: 100vh;
          padding: 24px;
          background: linear-gradient(
            180deg,
            #f8fbff 0%,
            #f3f7fc 100%
          );
        }

        .bs-auth-shell {
          min-height: calc(100vh - 48px);
          display: grid;
          grid-template-columns: 1.08fr 0.92fr;
          overflow: hidden;
          background: #ffffff;
          border: 1px solid #e7edf6;
          border-radius: 30px;
          box-shadow:
            0 25px 80px rgba(15, 23, 42, 0.08);
        }

        .bs-auth-left {
          position: relative;
          padding: 56px 58px;
          overflow: hidden;
          background:
            radial-gradient(
              circle at bottom right,
              rgba(49, 118, 255, 0.22),
              transparent 32%
            ),
            linear-gradient(
              135deg,
              #08245f 0%,
              #123c85 100%
            );
        }

        .bs-auth-left::before {
          position: absolute;
          inset: 0;
          content: "";
          opacity: 0.55;
          background-image:
            linear-gradient(
              rgba(255, 255, 255, 0.05) 1px,
              transparent 1px
            ),
            linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.05) 1px,
              transparent 1px
            );
          background-size: 46px 46px;
        }

        .bs-auth-left-inner {
          position: relative;
          z-index: 1;
          min-height: 100%;
          max-width: 660px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .bs-chip {
          width: fit-content;
          margin-bottom: 28px;
          padding: 11px 18px;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          color: #ffffff;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 1.8px;
        }

        .bs-chip span {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #ffffff;
        }

        .bs-auth-left h1 {
          margin: 0 0 24px;
          color: #ffffff;
          font-size: 64px;
          line-height: 1.06;
          font-weight: 700;
          letter-spacing: -1.6px;
        }

        .bs-left-subtitle {
          max-width: 620px;
          margin-bottom: 34px;
          color: rgba(255, 255, 255, 0.82);
          font-size: 17px;
          line-height: 1.8;
          font-weight: 400;
        }

        .bs-feature-stack {
          max-width: 640px;
          display: grid;
          gap: 16px;
        }

        .bs-feature-box {
          padding: 18px;
          display: flex;
          align-items: flex-start;
          gap: 15px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 22px;
          backdrop-filter: blur(8px);
        }

        .bs-feature-box-icon {
          width: 44px;
          height: 44px;
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          background: rgba(255, 255, 255, 0.12);
          border-radius: 14px;
          font-size: 20px;
        }

        .bs-feature-box-content h5 {
          margin: 0 0 4px;
          color: #ffffff;
          font-size: 16px;
          font-weight: 600;
        }

        .bs-feature-box-content p {
          margin: 0;
          color: rgba(255, 255, 255, 0.74);
          font-size: 14px;
          line-height: 1.65;
          font-weight: 400;
        }

        .bs-auth-right {
          min-width: 0;
          padding: 36px 34px 26px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          background:
            radial-gradient(
              circle at top right,
              rgba(40, 120, 229, 0.08),
              transparent 30%
            ),
            #f9fbfe;
        }

        .bs-auth-card {
          width: 100%;
          max-width: 460px;
          margin: 0 auto;
          padding: 32px 34px 28px;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid #e7edf6;
          border-radius: 26px;
          box-shadow:
            0 18px 48px rgba(15, 23, 42, 0.07);
        }

        .bs-logo-wrap {
          width: 100%;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .bs-logo-wrap img {
          display: block;
          width: 128px;
          max-width: 100%;
          max-height: 78px;
          margin: 0 auto;
          object-fit: contain;
          object-position: center;
        }

        .bs-auth-title {
          margin-bottom: 22px;
          text-align: center;
        }

        .bs-auth-title h2 {
          margin: 0 0 8px;
          color: #0a2354;
          font-size: 28px;
          line-height: 1.2;
          font-weight: 700;
        }

        .bs-auth-title p {
          margin: 0;
          color: #74839a;
          font-size: 14px;
          line-height: 1.7;
          font-weight: 400;
        }

        .bs-steps {
          margin-bottom: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bs-step-dot {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #73829a;
          background: #edf2f8;
          border-radius: 50%;
          font-size: 14px;
          font-weight: 600;
        }

        .bs-step-dot.active {
          color: #ffffff;
          background: #2f7be7;
        }

        .bs-step-dot.done {
          color: #ffffff;
          background: #10b981;
        }

        .bs-step-line {
          width: 80px;
          height: 2px;
          margin: 0 10px;
          background: #e3eaf4;
        }

        .bs-step-line.active {
          background: #2f7be7;
        }

        .bs-form-group {
          margin-bottom: 18px;
        }

        .bs-form-group label {
          margin-bottom: 8px;
          display: block;
          color: #0a2354;
          font-size: 14px;
          font-weight: 600;
        }

        .bs-phone-field {
          display: flex;
          align-items: center;
          overflow: hidden;
          background: #ffffff;
          border: 1px solid #dbe4f0;
          border-radius: 16px;
          transition: all 0.25s ease;
        }

        .bs-phone-field span {
          padding: 0 16px;
          color: #2f7be7;
          border-right: 1px solid #e7edf6;
          font-size: 14px;
          font-weight: 600;
        }

        .bs-phone-field input {
          width: 100%;
          padding: 15px 16px;
          color: #0a2354;
          background: #ffffff;
          border: none;
          outline: none;
          font-size: 15px;
          font-weight: 400;
        }

        .bs-phone-field input::placeholder {
          color: #97a3b5;
        }

        .bs-phone-field:focus-within {
          border-color: #2f7be7;
          box-shadow:
            0 0 0 4px rgba(47, 123, 231, 0.1);
        }

        .bs-otp-boxes {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .bs-otp-box {
          width: 100%;
          height: 58px;
          color: #0a2354;
          background: #ffffff;
          border: 1px solid #dbe4f0;
          border-radius: 16px;
          outline: none;
          font-size: 22px;
          font-weight: 600;
          text-align: center;
          transition: all 0.25s ease;
        }

        .bs-otp-box:focus {
          border-color: #2f7be7;
          box-shadow:
            0 0 0 4px rgba(47, 123, 231, 0.1);
        }

        .bs-main-btn {
          width: 100%;
          min-height: 50px;
          padding: 15px 18px;
          color: #ffffff;
          background: #2f7be7;
          border: none;
          border-radius: 16px;
          font-size: 15px;
          font-weight: 600;
          transition: all 0.25s ease;
          box-shadow:
            0 14px 30px rgba(47, 123, 231, 0.22);
        }

        .bs-main-btn:hover {
          background: #1e6fe1;
          transform: translateY(-1px);
        }

        .bs-main-btn:disabled {
          cursor: not-allowed;
          opacity: 0.75;
        }

        .bs-text-btn {
          width: 100%;
          padding-top: 12px;
          color: #68788f;
          background: transparent;
          border: none;
          font-size: 14px;
          font-weight: 500;
        }

        .bs-divider {
          margin: 22px 0 14px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .bs-divider span {
          height: 1px;
          flex: 1;
          background: #e7edf6;
        }

        .bs-divider p {
          margin: 0;
          white-space: nowrap;
          color: #98a5b7;
          font-size: 12px;
          font-weight: 400;
        }

        .bs-account-options {
          display: grid;
          gap: 10px;
        }

        .bs-account-option {
          width: 100%;
          padding: 13px 14px;
          display: flex;
          align-items: center;
          gap: 12px;
          color: inherit;
          background: #ffffff;
          border: 1px solid #e2e9f3;
          border-radius: 15px;
          text-align: left;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.22s ease;
        }

        .bs-account-option:hover {
          color: inherit;
          background: #f8fbff;
          border-color: #b9d3f7;
          text-decoration: none;
          transform: translateY(-1px);
          box-shadow:
            0 8px 22px rgba(15, 35, 84, 0.07);
        }

        .bs-account-icon {
          width: 38px;
          height: 38px;
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2f7be7;
          background: #edf5ff;
          border-radius: 11px;
          font-size: 17px;
        }

        .bs-account-content {
          min-width: 0;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .bs-account-content strong {
          color: #0a2354;
          font-size: 14px;
          line-height: 1.4;
          font-weight: 600;
        }

        .bs-account-content small {
          color: #7b899e;
          font-size: 11px;
          line-height: 1.5;
          font-weight: 400;
        }

        .bs-account-arrow {
          flex: 0 0 auto;
          color: #9aa8bb;
          font-size: 16px;
        }

        .bs-guest-link {
          width: 100%;
          margin-top: 16px;
          padding: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          color: #64748b;
          background: transparent;
          border: none;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .bs-guest-link:hover {
          color: #2f7be7;
        }

        .bs-auth-footer {
          max-width: 520px;
          margin: 20px auto 0;
          text-align: center;
        }

        .bs-footer-links {
          margin-bottom: 8px;
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 10px 16px;
        }

        .bs-footer-links a {
          color: #73829a;
          font-size: 12px;
          font-weight: 400;
          text-decoration: none;
        }

        .bs-footer-links a:hover {
          color: #2f7be7;
        }

        .bs-auth-footer p {
          margin: 0;
          color: #9aa7b9;
          font-size: 12px;
          font-weight: 400;
        }

        @media (max-width: 1280px) {
          .bs-auth-left h1 {
            font-size: 54px;
          }
        }

        @media (max-width: 1199px) {
          .bs-auth-shell {
            grid-template-columns: 1fr;
          }

          .bs-auth-left {
            display: none;
          }

          .bs-auth-right {
            min-height: calc(100vh - 48px);
          }
        }

        @media (max-width: 575px) {
          .bs-auth-page {
            padding: 0;
            background: #ffffff;
          }

          .bs-auth-shell {
            min-height: 100vh;
            border: none;
            border-radius: 0;
            box-shadow: none;
          }

          .bs-auth-right {
            min-height: 100vh;
            padding: 20px 16px;
          }

          .bs-auth-card {
            padding: 26px 20px;
            border-radius: 24px;
          }

          .bs-logo-wrap img {
            width: 116px;
          }

          .bs-auth-title h2 {
            font-size: 24px;
          }

          .bs-step-line {
            width: 62px;
          }

          .bs-otp-box {
            height: 54px;
            border-radius: 14px;
            font-size: 20px;
          }

          .bs-account-option {
            padding: 12px;
          }

          .bs-account-content strong {
            font-size: 13px;
          }

          .bs-account-content small {
            font-size: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;